/**
 * Agent Harness Page
 * Executive-area board that renders the company's functional agents and their
 * core skills / responsibilities as a getsequence.io-style node board, styled
 * with the OPS "Refined Industrial" tokens.
 *
 * Single source of truth: functions/src/agentHarness.ts -> /docs/agents/harness.json
 * (falls back to the live agentRegistryApi?action=harness). The semantic content
 * comes from the harness; this page only adds presentation (layout/icons/colors).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  NodeProps,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Calendar,
  Inbox,
  Gavel,
  FolderOpen,
  Megaphone,
  Building2,
  ArrowLeft,
  ExternalLink,
  X,
  Sparkles,
  CheckCircle2,
  UserPlus,
  BookOpen,
  LayoutGrid,
  FileJson,
  FileText,
  Plane,
  NotebookPen,
  ClipboardList,
  Activity,
  TrendingUp,
  Handshake,
  HardHat,
  DollarSign,
  Trees,
  Hammer,
  ShoppingCart,
  Landmark,
} from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './AgentHarnessPage.css';

const HARNESS_STATIC_URL = '/docs/agents/harness.json';
const DOCS_INDEX_URL = '/docs/agents/docs-index.json';
const HARNESS_API_URL =
  'https://us-central1-lctf-projects.cloudfunctions.net/agentRegistryApi?action=harness';

type AgentDocKind = 'api' | 'contract' | 'catalog' | 'harness';

interface AgentDocEntry {
  id: string;
  title: string;
  kind: AgentDocKind;
  description: string;
  jsonUrl: string;
  markdownUrl?: string;
}

interface LinkedApi {
  id: string;
  name: string;
  endpoint: string;
  guideJsonUrl: string;
  actions?: { name: string; description: string }[];
}

interface HarnessAgent {
  id: string;
  name: string;
  domain: string;
  summary: string;
  coreSkills: string[];
  responsibilities: string[];
  status: 'live' | 'beta';
  connections: string[];
  endpoints: string[];
  linkedApis: LinkedApi[];
}

interface HarnessPayload {
  success: boolean;
  harnessVersion: string;
  generatedAt: string;
  description?: string;
  catalogUrl?: string;
  documentation?: AgentDocEntry[];
  agents: HarnessAgent[];
}

type HarnessView = 'board' | 'docs';

const DOC_KIND_LABELS: Record<AgentDocKind, string> = {
  catalog: 'Indexes',
  harness: 'Indexes',
  api: 'API Guides',
  contract: 'Contracts',
};

function groupDocumentation(docs: AgentDocEntry[]): { label: string; items: AgentDocEntry[] }[] {
  const indexes = docs.filter((d) => d.kind === 'catalog' || d.kind === 'harness');
  const apis = docs.filter((d) => d.kind === 'api');
  const contracts = docs.filter((d) => d.kind === 'contract');
  const groups: { label: string; items: AgentDocEntry[] }[] = [];
  if (indexes.length) groups.push({ label: 'Indexes', items: indexes });
  if (apis.length) groups.push({ label: DOC_KIND_LABELS.api, items: apis });
  if (contracts.length) groups.push({ label: DOC_KIND_LABELS.contract, items: contracts });
  return groups;
}

interface AgentLayout {
  position: { x: number; y: number };
  icon: React.ReactNode;
  accent: string;
}

/** Presentation layer — keyed by harness agent id (kept out of the API payload).
 *  Fixed cell size: COL=320, ROW=260 so 240px-wide cards cannot overlap. */
const COL = 320;
const ROW = 260;
const LAYOUT_REVISION = 'grid-v4';

const HARNESS_LAYOUT: Record<string, AgentLayout> = {
  // Row 0 — executive + inbound
  calendar: { position: { x: 0 * COL, y: 0 * ROW }, icon: <Calendar size={18} />, accent: '#3D5A40' },
  intake: { position: { x: 1 * COL, y: 0 * ROW }, icon: <UserPlus size={18} />, accent: '#2563EB' },
  inbox: { position: { x: 2 * COL, y: 0 * ROW }, icon: <Inbox size={18} />, accent: '#2C3E2F' },
  'portal-watch': { position: { x: 3 * COL, y: 0 * ROW }, icon: <Activity size={18} />, accent: '#B45309' },
  // Row 1 — travel / ops (hub sits in the open center)
  travel: { position: { x: 0 * COL, y: 1 * ROW }, icon: <Plane size={18} />, accent: '#3A6B7C' },
  'ops-tasks': { position: { x: 3 * COL, y: 1 * ROW }, icon: <ClipboardList size={18} />, accent: '#5C6B4A' },
  // Row 2 — documents / market / sales
  files: { position: { x: 0 * COL, y: 2 * ROW }, icon: <FolderOpen size={18} />, accent: '#4F6172' },
  marketing: { position: { x: 2 * COL, y: 2 * ROW }, icon: <Megaphone size={18} />, accent: '#7C5A4A' },
  sales: { position: { x: 3 * COL, y: 2 * ROW }, icon: <TrendingUp size={18} />, accent: '#6B4C9A' },
  // Row 3 — delivery / company brain
  'meeting-notes': { position: { x: 0 * COL, y: 3 * ROW }, icon: <NotebookPen size={18} />, accent: '#4A6670' },
  bidding: { position: { x: 1 * COL, y: 3 * ROW }, icon: <Gavel size={18} />, accent: '#9A6A2F' },
  'proposal-review': { position: { x: 3 * COL, y: 3 * ROW }, icon: <FileText size={18} />, accent: '#2C3E2F' },
  deals: { position: { x: 2 * COL, y: 3 * ROW }, icon: <Handshake size={18} />, accent: '#5B6E4A' },
  // Row 4
  projects: { position: { x: 1 * COL, y: 4 * ROW }, icon: <HardHat size={18} />, accent: '#6B5B4A' },
  'materials-price': { position: { x: 2 * COL, y: 4 * ROW }, icon: <DollarSign size={18} />, accent: '#3F6B5A' },
  'timber-list': { position: { x: 3 * COL, y: 4 * ROW }, icon: <Trees size={18} />, accent: '#4A6B52' },
  'trade-briefing': { position: { x: 0 * COL, y: 4 * ROW }, icon: <Hammer size={18} />, accent: '#2C3E2F' },
  'inspiration-board': { position: { x: 1 * COL, y: 5 * ROW }, icon: <LayoutGrid size={18} />, accent: '#8B6914' },
  'designer-invoice': { position: { x: 0 * COL, y: 5 * ROW }, icon: <FileText size={18} />, accent: '#3D5A40' },
  purchasing: { position: { x: 2 * COL, y: 5 * ROW }, icon: <ShoppingCart size={18} />, accent: '#7C5A4A' },
  finance: { position: { x: 3 * COL, y: 5 * ROW }, icon: <Landmark size={18} />, accent: '#2C4A3E' },
};

const HUB_POSITION = { x: 1.35 * COL, y: 1.35 * ROW };
const FALLBACK_LAYOUT: AgentLayout = {
  position: { x: 0, y: 0 },
  icon: <Sparkles size={18} />,
  accent: '#6B6960',
};

// ── Custom nodes ───────────────────────────────────────────

interface AgentNodeData {
  name: string;
  domain: string;
  status: 'live' | 'beta';
  skills: string[];
  accent: string;
  icon: React.ReactNode;
  dimmed?: boolean;
  active?: boolean;
}

const AgentNode: React.FC<NodeProps<AgentNodeData>> = ({ data }) => (
  <div
    className={`harness-node${data.dimmed ? ' harness-node-dimmed' : ''}${data.active ? ' harness-node-active' : ''}`}
    style={{ ['--node-accent' as any]: data.accent }}
  >
    <Handle type="target" position={Position.Top} className="harness-handle" />
    <div className="harness-node-head">
      <span className="harness-node-icon">{data.icon}</span>
      <div className="harness-node-titles">
        <span className="harness-node-name">{data.name}</span>
        <span className="harness-node-domain">{data.domain}</span>
      </div>
      <span className={`harness-node-status harness-node-status-${data.status}`}>{data.status}</span>
    </div>
    <ul className="harness-node-skills">
      {data.skills.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ul>
    <Handle type="source" position={Position.Bottom} className="harness-handle" />
  </div>
);

const HubNode: React.FC<NodeProps<{ label: string; sub: string }>> = ({ data }) => (
  <div className="harness-hub">
    <Handle type="target" position={Position.Top} className="harness-handle" />
    <Handle type="source" position={Position.Bottom} className="harness-handle" />
    <span className="harness-hub-icon"><Building2 size={20} /></span>
    <span className="harness-hub-label">{data.label}</span>
    <span className="harness-hub-sub">{data.sub}</span>
  </div>
);

const nodeTypes = { agent: AgentNode, hub: HubNode };

/** Re-fit the viewport when the harness layout revision / agent set changes. */
const HarnessFitView: React.FC<{ trigger: string }> = ({ trigger }) => {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      fitView({ padding: 0.16, minZoom: 0.3, maxZoom: 1.1 });
    });
    return () => cancelAnimationFrame(id);
  }, [trigger, fitView]);
  return null;
};

function localDocPath(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin === window.location.origin) return parsed.pathname;
    const hosted = parsed.pathname.match(/\/docs\/agents\/.+/);
    return hosted ? hosted[0] : url;
  } catch {
    return url.startsWith('/') ? url : `/docs/agents/${url}`;
  }
}

// ── Board ──────────────────────────────────────────────────

const HUB_ID = '__hub__';

const AgentHarnessBoard: React.FC = () => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState<HarnessPayload | null>(null);
  const [documentation, setDocumentation] = useState<AgentDocEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<HarnessView>('board');
  const [previewDoc, setPreviewDoc] = useState<AgentDocEntry | null>(null);
  const [previewJson, setPreviewJson] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let res = await fetch(HARNESS_STATIC_URL, { cache: 'no-cache' });
        if (!res.ok) res = await fetch(HARNESS_API_URL);
        if (!res.ok) throw new Error(`Failed to load harness (${res.status})`);
        const json = (await res.json()) as HarnessPayload;
        if (!cancelled) {
          setPayload(json);
          if (json.documentation?.length) setDocumentation(json.documentation);
        }
      } catch (e) {
        try {
          const res = await fetch(HARNESS_API_URL);
          const json = (await res.json()) as HarnessPayload;
          if (!cancelled) {
            setPayload(json);
            if (json.documentation?.length) setDocumentation(json.documentation);
          }
        } catch (e2) {
          if (!cancelled) setError((e2 as Error).message || 'Failed to load Agent Harness');
        }
      }

      try {
        const docsRes = await fetch(DOCS_INDEX_URL, { cache: 'no-cache' });
        if (docsRes.ok) {
          const docsJson = (await docsRes.json()) as { documentation?: AgentDocEntry[] };
          if (!cancelled && docsJson.documentation?.length) {
            setDocumentation(docsJson.documentation);
          }
        }
      } catch {
        // harness payload documentation is the fallback
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const openDocPreview = useCallback(async (doc: AgentDocEntry) => {
    setPreviewDoc(doc);
    setPreviewJson(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const res = await fetch(localDocPath(doc.jsonUrl), { cache: 'no-cache' });
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const json = await res.json();
      setPreviewJson(JSON.stringify(json, null, 2));
    } catch (e) {
      setPreviewError((e as Error).message || 'Failed to load document');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const agents = payload?.agents ?? [];
  const docGroups = useMemo(() => groupDocumentation(documentation), [documentation]);
  const selected = useMemo(
    () => agents.find((a) => a.id === selectedId) ?? null,
    [agents, selectedId],
  );

  const builtNodes: Node[] = useMemo(() => {
    if (!agents.length) return [];
    const related = new Set<string>();
    if (selectedId) {
      related.add(selectedId);
      const sel = agents.find((a) => a.id === selectedId);
      sel?.connections.forEach((c) => related.add(c));
    }
    const focusActive = Boolean(selectedId);
    const hub: Node = {
      id: HUB_ID,
      type: 'hub',
      position: HUB_POSITION,
      data: { label: 'Executive Suite', sub: 'Orchestration' },
      draggable: false,
      selectable: false,
    };
    const agentNodes: Node[] = agents.map((a, index) => {
      const layout = HARNESS_LAYOUT[a.id] ?? {
        ...FALLBACK_LAYOUT,
        position: { x: (index % 4) * COL, y: Math.floor(index / 4) * ROW },
      };
      return {
        id: a.id,
        type: 'agent',
        position: layout.position,
        draggable: false,
        data: {
          name: a.name,
          domain: a.domain,
          status: a.status,
          skills: a.coreSkills.slice(0, 2),
          accent: layout.accent,
          icon: layout.icon,
          active: selectedId === a.id,
          dimmed: focusActive && !related.has(a.id),
        } as AgentNodeData,
      };
    });
    return [hub, ...agentNodes];
  }, [agents, selectedId]);

  const builtEdges: Edge[] = useMemo(() => {
    if (!agents.length || !selectedId) return [];
    const selectedAgent = agents.find((a) => a.id === selectedId);
    if (!selectedAgent) return [];
    const ids = new Set(agents.map((a) => a.id));
    const accent = HARNESS_LAYOUT[selectedAgent.id]?.accent ?? FALLBACK_LAYOUT.accent;
    return selectedAgent.connections
      .filter((c) => ids.has(c))
      .map((c) => ({
        id: `link-${selectedAgent.id}-${c}`,
        source: selectedAgent.id,
        target: c,
        type: 'smoothstep',
        animated: true,
        label: 'collaborates',
        labelStyle: {
          fontSize: 10,
          fontWeight: 600,
          fill: accent,
          fontFamily: 'inherit',
        },
        labelBgStyle: { fill: '#F7F5F0', fillOpacity: 0.95 },
        labelBgPadding: [4, 6] as [number, number],
        labelBgBorderRadius: 4,
        style: { stroke: accent, strokeWidth: 2 },
      }));
  }, [agents, selectedId]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    setNodes(builtNodes);
  }, [builtNodes, setNodes]);

  useEffect(() => {
    setEdges(builtEdges);
  }, [builtEdges, setEdges]);

  const collaboratorAgents = useMemo(() => {
    if (!selected) return [];
    const byId = new Map(agents.map((a) => [a.id, a]));
    return selected.connections
      .map((id) => byId.get(id))
      .filter((a): a is HarnessAgent => Boolean(a));
  }, [agents, selected]);

  const onNodeClick = useCallback((_e: React.MouseEvent, node: Node) => {
    if (node.id === HUB_ID) {
      setSelectedId(null);
      return;
    }
    setSelectedId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedId(null);
  }, []);

  const flowKey = `${payload?.harnessVersion ?? 'loading'}-${LAYOUT_REVISION}-${agents.length}`;

  return (
    <div className="ops-hub harness-page">
      <AppHeader />
      <div className="harness-main">
        <div className="harness-toolbar">
          <div className="harness-toolbar-left">
            <button className="harness-back" onClick={() => navigate('/executive')}>
              <ArrowLeft size={15} />
              <span>Executive</span>
            </button>
            <h1 className="harness-title">Agent Harness</h1>
            <span className="harness-subtitle">
              Source of truth for agents and their abilities
            </span>
            {view === 'board' && (
              <span className="harness-legend" title="Click an agent to see who it collaborates with">
                Click a card — lines show collaborators only
              </span>
            )}
          </div>
          <div className="harness-toolbar-right">
            <div className="harness-view-toggle" role="tablist" aria-label="Harness view">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'board'}
                className={`harness-view-btn${view === 'board' ? ' active' : ''}`}
                onClick={() => setView('board')}
              >
                <LayoutGrid size={14} />
                <span>Board</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'docs'}
                className={`harness-view-btn${view === 'docs' ? ' active' : ''}`}
                onClick={() => setView('docs')}
              >
                <BookOpen size={14} />
                <span>Documentation</span>
                {documentation.length > 0 && (
                  <span className="harness-view-count">{documentation.length}</span>
                )}
              </button>
            </div>
            {payload && (
              <span className="harness-version">v{payload.harnessVersion}</span>
            )}
            <a
              className="harness-docs-link"
              href="/docs/agents/catalog.json"
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={13} />
              <span>catalog.json</span>
            </a>
          </div>
        </div>

        <div className={`harness-canvas${selected ? ' detail-open' : ''}${view === 'docs' ? ' docs-mode' : ''}`}>
          {loading && (
            <div className="harness-loading">
              <div className="harness-loading-bar" />
            </div>
          )}
          {error && !loading && view === 'board' && (
            <div className="harness-error">Could not load Agent Harness: {error}</div>
          )}
          {!loading && view === 'board' && !error && (
            <ReactFlow
              key={flowKey}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable
              onInit={(instance) => {
                requestAnimationFrame(() => {
                  instance.fitView({ padding: 0.16, minZoom: 0.3, maxZoom: 1.1 });
                });
              }}
              minZoom={0.2}
              maxZoom={1.75}
              proOptions={{ hideAttribution: true }}
            >
              <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} color="#D8D5CE" />
              <Controls showInteractive={false} />
              <HarnessFitView trigger={flowKey} />
            </ReactFlow>
          )}
          {!loading && view === 'docs' && (
            <div className="harness-docs-panel">
              <div className="harness-docs-intro">
                <h2 className="harness-docs-title">Agent documentation</h2>
                <p className="harness-docs-lead">
                  Machine-readable contracts for Mac-mini agents, integrations, and the Executive harness.
                  Generated from <code>functions/src/agentRegistry.ts</code> — sync via{' '}
                  <code>npm run build-agent-catalog</code>.
                </p>
              </div>
              {docGroups.length === 0 ? (
                <div className="harness-docs-empty">No documentation index found. Run build-agent-catalog.</div>
              ) : (
                docGroups.map((group) => (
                  <section key={group.label} className="harness-docs-group">
                    <h3 className="harness-docs-group-label">{group.label}</h3>
                    <div className="harness-docs-grid">
                      {group.items.map((doc) => (
                        <article key={doc.id} className="harness-doc-card">
                          <div className="harness-doc-card-head">
                            <span className="harness-doc-kind">{doc.kind}</span>
                            <h4 className="harness-doc-card-title">{doc.title}</h4>
                          </div>
                          <p className="harness-doc-card-desc">{doc.description}</p>
                          <div className="harness-doc-card-actions">
                            <button
                              type="button"
                              className="harness-doc-btn"
                              onClick={() => openDocPreview(doc)}
                            >
                              <FileJson size={13} />
                              <span>Preview JSON</span>
                            </button>
                            <a
                              className="harness-doc-btn"
                              href={localDocPath(doc.jsonUrl)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <ExternalLink size={13} />
                              <span>Open JSON</span>
                            </a>
                            {doc.markdownUrl && (
                              <a
                                className="harness-doc-btn"
                                href={localDocPath(doc.markdownUrl)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FileText size={13} />
                                <span>Markdown</span>
                              </a>
                            )}
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {previewDoc && (
        <div className="harness-detail-overlay" onClick={() => setPreviewDoc(null)}>
          <aside
            className="harness-detail harness-doc-preview"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="harness-detail-close" onClick={() => setPreviewDoc(null)}>
              <X size={18} />
            </button>
            <div className="harness-detail-head">
              <span className="harness-detail-icon">
                <FileJson size={20} />
              </span>
              <div>
                <h2 className="harness-detail-name">{previewDoc.title}</h2>
                <span className="harness-detail-domain">{previewDoc.id}.json</span>
              </div>
            </div>
            <p className="harness-detail-summary">{previewDoc.description}</p>
            {previewLoading && <div className="harness-doc-preview-loading">Loading…</div>}
            {previewError && <div className="harness-doc-preview-error">{previewError}</div>}
            {previewJson && (
              <pre className="harness-doc-preview-code">{previewJson}</pre>
            )}
            <div className="harness-doc-preview-footer">
              <a
                className="harness-endpoint-guide"
                href={localDocPath(previewDoc.jsonUrl)}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink size={12} />
                <span>Open in new tab</span>
              </a>
            </div>
          </aside>
        </div>
      )}

      {selected && (
        <div className="harness-detail-overlay" onClick={() => setSelectedId(null)}>
          <aside
            className="harness-detail"
            style={{ ['--node-accent' as any]: HARNESS_LAYOUT[selected.id]?.accent ?? FALLBACK_LAYOUT.accent }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="harness-detail-close" onClick={() => setSelectedId(null)}>
              <X size={18} />
            </button>
            <div className="harness-detail-head">
              <span className="harness-detail-icon">
                {HARNESS_LAYOUT[selected.id]?.icon ?? FALLBACK_LAYOUT.icon}
              </span>
              <div>
                <h2 className="harness-detail-name">{selected.name}</h2>
                <span className="harness-detail-domain">{selected.domain}</span>
              </div>
              <span className={`harness-node-status harness-node-status-${selected.status}`}>
                {selected.status}
              </span>
            </div>

            <p className="harness-detail-summary">{selected.summary}</p>

            {collaboratorAgents.length > 0 && (
              <div className="harness-detail-section">
                <h3 className="harness-detail-label">Collaborates with</h3>
                <p className="harness-detail-hint">
                  Agents this one hands work to or shares context with (shown as lines on the board).
                </p>
                <div className="harness-collab-chips">
                  {collaboratorAgents.map((peer) => (
                    <button
                      key={peer.id}
                      type="button"
                      className="harness-collab-chip"
                      style={{ ['--node-accent' as any]: HARNESS_LAYOUT[peer.id]?.accent ?? FALLBACK_LAYOUT.accent }}
                      onClick={() => setSelectedId(peer.id)}
                    >
                      <span className="harness-collab-chip-icon">
                        {HARNESS_LAYOUT[peer.id]?.icon ?? FALLBACK_LAYOUT.icon}
                      </span>
                      <span className="harness-collab-chip-text">
                        <span className="harness-collab-chip-name">{peer.name}</span>
                        <span className="harness-collab-chip-domain">{peer.domain}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="harness-detail-section">
              <h3 className="harness-detail-label">Core skills</h3>
              <ul className="harness-detail-list">
                {selected.coreSkills.map((s, i) => (
                  <li key={i}>
                    <Sparkles size={13} className="harness-detail-bullet" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="harness-detail-section">
              <h3 className="harness-detail-label">Responsibilities</h3>
              <ul className="harness-detail-list">
                {selected.responsibilities.map((r, i) => (
                  <li key={i}>
                    <CheckCircle2 size={13} className="harness-detail-bullet" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(selected.linkedApis.length > 0 || selected.endpoints.length > 0) && (
              <div className="harness-detail-section">
                <h3 className="harness-detail-label">Endpoints &amp; contracts</h3>
                <div className="harness-endpoints">
                  {selected.linkedApis.map((api) => (
                    <div key={api.id} className="harness-endpoint">
                      <span className="harness-endpoint-name">{api.name}</span>
                      <code className="harness-endpoint-url">{api.endpoint}</code>
                      <div className="harness-endpoint-links">
                        <a
                          className="harness-endpoint-guide"
                          href={localDocPath(api.guideJsonUrl)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink size={12} />
                          <span>Guide JSON</span>
                        </a>
                        <button
                          type="button"
                          className="harness-endpoint-guide harness-endpoint-guide-btn"
                          onClick={() => {
                            const doc = documentation.find((d) => d.id === api.id);
                            if (doc) {
                              setView('docs');
                              void openDocPreview(doc);
                            }
                          }}
                        >
                          <FileJson size={12} />
                          <span>Preview</span>
                        </button>
                      </div>
                      {api.actions && api.actions.length > 0 && (
                        <ul className="harness-endpoint-actions">
                          {api.actions.map((act) => (
                            <li key={act.name}>
                              <code className="harness-action-name">{act.name}</code>
                              <span className="harness-action-desc">{act.description}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {selected.endpoints.map((ep, i) => (
                    <div key={i} className="harness-endpoint">
                      <code className="harness-endpoint-url">{ep}</code>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {documentation.length > 0 && (
              <div className="harness-detail-section">
                <h3 className="harness-detail-label">All documentation</h3>
                <button
                  type="button"
                  className="harness-detail-docs-link"
                  onClick={() => {
                    setSelectedId(null);
                    setView('docs');
                  }}
                >
                  <BookOpen size={14} />
                  <span>Browse {documentation.length} agent docs</span>
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

const AgentHarnessPage: React.FC = () => (
  <ReactFlowProvider>
    <AgentHarnessBoard />
  </ReactFlowProvider>
);

export default AgentHarnessPage;
